import { Alert, Paper, Text, Progress, Stack, Group } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type CoverageCardProps = {
  matchedProducts: number;
  totalFeedProducts: number;
  unmatchedProducts: number;
  coveragePercentage: number;
};

function ProductCount({ matched, total }: { matched: number; total: number }) {
  return (
    <Text size="xl">
      <Text component="span" size="displaySm" fw={FONT_WEIGHTS.bold}>
        {matched}
      </Text>{' '}
      / {total} products
    </Text>
  );
}

function CoverageBreakdown({ coveragePercentage }: { coveragePercentage: number }) {
  const isPartialCoverage = coveragePercentage > 0 && coveragePercentage < 100;
  if (!isPartialCoverage) return null;

  return (
    <>
      <Progress value={coveragePercentage} size="lg" radius="md" color="brand.6" />
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {coveragePercentage.toFixed(2)}% matched
        </Text>
        <Text size="sm" c="dimmed">
          {(100 - coveragePercentage).toFixed(2)}% unmatched
        </Text>
      </Group>
    </>
  );
}

function CoverageAlert({
  matchedProducts,
  unmatchedProducts,
}: Pick<CoverageCardProps, 'matchedProducts' | 'unmatchedProducts'>) {
  if (matchedProducts === 0) {
    return (
      <Alert icon={<IconAlertTriangle />} color="yellow" variant="light">
        <Text size="sm">
          None of your feed products are currently matched to your book. Review your provider
          connections to start linking products.
        </Text>
      </Alert>
    );
  }

  if (unmatchedProducts > 0) {
    return (
      <Alert icon={<IconInfoCircle />} color="yellow" variant="light">
        <Text size="sm">
          <Text component="span" fw={FONT_WEIGHTS.bold} c="brand.6">
            {unmatchedProducts} products
          </Text>{' '}
          are not yet connected. Adding provider feeds will increase your coverage and connect more
          of your book.
        </Text>
      </Alert>
    );
  }

  return (
    <Alert icon={<IconCircleCheck />} color="green" variant="light">
      <Text size="sm">Your full book is connected and receiving daily valuations.</Text>
    </Alert>
  );
}

export default function CoverageCard({
  matchedProducts,
  totalFeedProducts,
  unmatchedProducts,
  coveragePercentage,
}: CoverageCardProps) {
  return (
    <Paper withBorder p="md" radius="md" h="100%">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Book Coverage
        </Text>
        <ProductCount matched={matchedProducts} total={totalFeedProducts} />
        <CoverageBreakdown coveragePercentage={coveragePercentage} />
        <CoverageAlert matchedProducts={matchedProducts} unmatchedProducts={unmatchedProducts} />
      </Stack>
    </Paper>
  );
}
